import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { X } from 'lucide-react';
import CompanionSnapshot from './CompanionSnapshot';
import CompanionRailBlocks from './CompanionRailBlocks';

import { hasCompanion } from '@/components/companion/access';

/**
 * The right-edge tab. Closed it reads "<FirstName>'s Companion"; open it reads "FULL" and opens
 * the full page. Deliberately compact — mixed case at tight tracking, one column instead of two,
 * so it takes less of the edge than the old wide-tracked "COMPANION | NAME" pair.
 * Mounted once in DashboardLayout so it follows the participant across every dashboard page.
 */
export const CompanionTab = ({ user }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const firstName = (user?.full_name || user?.username || '').trim().split(' ')[0];
  const shortName = firstName.length > 12 ? firstName.slice(0, 12) : firstName;
  const tabLabel = shortName ? `${shortName}'s Companion` : 'Your Companion';

  const load = useCallback(() => {
    axios.get(`${API}/assessments/my/companion`)
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => { if (open && !data && !failed) load(); }, [open, data, failed, load]);

  const openFull = () => {
    setOpen(false);          // otherwise the panel sits on top of the page it just opened
    navigate('/dashboard/companion');
  };

  if (!hasCompanion(user)) return null;

  return (
    <>
      {/* z-[60] on purpose: the panel below is z-40, and at equal levels the later DOM node
          would paint over the tabs and swallow the FULL/CLOSE clicks. */}
      <div className="companion-side-rail fixed right-0 top-1/2 -translate-y-1/2 z-[60] flex flex-col gap-2 items-end">
        <button
          onClick={() => (open ? openFull() : setOpen(true))}
          className="group flex items-center rounded-l-lg border border-r-0 border-cyan-300/30
                     bg-[#0b0e20]/90 py-2.5 pl-1.5 pr-1 backdrop-blur transition-colors duration-200
                     hover:border-cyan-300/60 hover:bg-[#111536]/90"
          data-testid={open ? 'companion-tab-full' : 'companion-tab-open'}
          aria-label={open ? 'Open the full Consciousness Companion' : 'Open your Consciousness Companion'}
        >
          <span
            className="text-[10px] font-medium tracking-[0.02em] text-cyan-200 whitespace-nowrap"
            style={{ writingMode: 'vertical-rl' }}
            data-testid="companion-tab-name"
          >
            {open ? 'FULL' : tabLabel}
          </span>
        </button>

        {open && (
          <button
            onClick={() => setOpen(false)}
            className="flex items-center rounded-l-lg border border-r-0 border-white/20
                       bg-[#0b0e20]/90 py-2 pl-1.5 pr-1 backdrop-blur hover:bg-white/10
                       transition-colors duration-200"
            data-testid="companion-tab-close"
            aria-label="Close the Companion panel"
          >
            <span
              className="text-[10px] font-medium tracking-[0.1em] text-white/60"
              style={{ writingMode: 'vertical-rl' }}
            >
              CLOSE
            </span>
          </button>
        )}
      </div>

      {open && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="companion-panel-backdrop"
          />
          <aside
            className="fixed right-0 top-0 z-40 h-full w-full max-w-md overflow-y-auto
                       border-l border-white/10 bg-[#080b18]/95 backdrop-blur-xl
                       p-6 pr-14 shadow-2xl"
            data-testid="companion-panel"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-base md:text-lg font-semibold text-white">
                  {firstName ? `${firstName}'s Consciousness Companion`
                             : 'Your Consciousness Companion'}
                </h2>
                <p className="text-sm text-white/50">Your own space</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/40 hover:text-white transition-colors duration-200"
                data-testid="companion-panel-x"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <CompanionRailBlocks onOpenFull={() => setOpen(false)} />

            <section className="pt-5 mt-5 border-t border-white/10" data-testid="rail-progression">
              <h3 className="text-xs uppercase tracking-wider text-white/40 mb-3">
                Consciousness Progression
              </h3>
              {failed && (
                <p className="text-sm text-white/50" data-testid="companion-panel-error">
                  Your progression could not be loaded just now.
                </p>
              )}
              {!data && !failed && (
                <p className="text-sm text-white/50">Opening your snapshot…</p>
              )}
              {data && (
                <CompanionSnapshot
                  data={data}
                  onOpenFull={openFull}
                />
              )}
            </section>
          </aside>
        </>
      )}
    </>
  );
};

export default CompanionTab;
